import {Injectable} from '@angular/core';

import {environment} from '../../../environments/environment';
import {
	Account,
	AccountCollection, AccountPostRequest, AccountPutRequest,
	Client,
	ClientCollection, ResetUserPasswordRequest, TagPostRequest,
	User,
	UserCollection,
	UserPostRequest,
	UserPutRequest,
	ContactPostRequest,
	ContactCollection,
	Contact,
	SiteCollection,
	Partner,
    AccessPolicy,
    Statement,
	PolicyPostRequest,
	Tag,
	PolicyPutRequest,
	PolicyCollection,
	ClientPostRequest,
	PolicyControlPostRequest
} from '@mitel/cloudlink-sdk/admin';
import {AdminService as CloudAdmin, Config, HttpMethod, HttpMethodRequest, HttpResponse, Odata, Utils} from '@mitel/cloudlink-sdk';
import {GroupPostRequest, GroupCollection} from "@mitel/cloudlink-sdk/admin";
import {ErrorsHandlerService} from './errors-handler.service';
import {accountMessages, commonErrorMessages, commonErrorNames, commonMessages, contactMessages} from '../constants/constants';
import {SpinnerService} from './spinner.service';
import {NotifierService} from 'angular-notifier';
import {TranslateService} from '@ngx-translate/core';
import {UserData} from '../interfaces/user-data';
import * as _ from 'lodash';
import { Util } from '../utils/Util';
import LRUCache from 'lru-cache';
import { QueryParam } from './user-templates.service';

const logContext = 'AdminService';

export interface EntitiesCollection<T>  { 
  count?: number;
  _links?: { self: string};
  _embedded?:{  items: Array<T>; }
}

@Injectable({providedIn: 'root'})
export class AdminService {
	private readonly api: CloudAdmin;
	private accountTagsCache = new LRUCache<string, Promise<any>>({
		ttl: 60 * 1000,  // ms (cache item expires in 60 sec)
		max: 10          // and, oldest item in cache is bumped if more than this items in cache
		// https://github.com/isaacs/node-lru-cache#readme
	}); // cache by account ID
	private adminServiceURL: any;

	constructor(
		private errorHandlerSvc: ErrorsHandlerService,
		private spinnerService: SpinnerService,
		private notifier: NotifierService,
		private translateService: TranslateService
	) {
		Config.cloud = environment.cloud;
		this.api = new CloudAdmin();

    this.adminServiceURL = {
      baseUrl: environment.adminURL,
      httpService: <HttpMethod>Config.httpService,

      async getPoliciesByType(type: string, queryParams?: QueryParam): Promise<EntitiesCollection<AccessPolicy>> {
        let url = `${this.baseUrl}/policies`;
        queryParams = { type: type, ...queryParams } as any;
        if (queryParams.$skipToken) {
          // rootUrl: 'https://admin.dev.api.mitel.io/2017-09-01'
          // skipToken: starts with /2017-09-01
          let lastSlash = this.baseUrl.lastIndexOf('/');
          url = this.baseUrl.substring(0, lastSlash) + queryParams.$skipToken;
        }
        else {
          if (queryParams) {
            url = this.addQueryParamsToUrl(url, queryParams);
          }
        }
        let httpRequest: HttpMethodRequest = {
          url: url
        }

        try {
          let getPoliciesResponse: HttpResponse = await this.httpService.get(httpRequest);
          return getPoliciesResponse.body;
        } catch (error) {
          throw error;
        }
      },
      addQueryParamsToUrl(url: string, queryParams: any): string {
        let firstParam: boolean = true;

        _.forEach(queryParams, (value, qParam) => {
          if (firstParam) {
            url = `${url}?`;
            firstParam = false;
          } else {
            url = `${url}&`;
          }
          url = `${url}${qParam}=${value}`;
        });
        return url;
      }
    }
	}
	// This method cannot use $SearchKeys parameter, as it is utilizing Odata as restriction.
	//So far only getPartnerUsers is using it. getUsers is not using this method.
	async getUsersByAccountId(accountId: string, odata?: Odata, options?: any): Promise<User[]> {
		return new Promise<User[]>(async (resolve, reject) => {
			let users: User[];
			try {
				this.spinnerService.showSpinner();		
				this.spinnerService.setSpinnerMessageByKey('spinnerMessages.fetchingUsers');
				const userCollection = await this.getUsersFromAccount(accountId, odata, options);
				this.spinnerService.hideSpinner();
				if (userCollection) {
					users = Utils.getItemsFromCollection(userCollection);
					const next = Utils.getOdataNext(userCollection);
					console.log('Function: , users: ', users);
					if (next) {
						const moreUsers = await this.getUsersByAccountId(accountId, next, options);
						users = users.concat(moreUsers);
					}
				}
				resolve(users);
			} catch (err) {
				this.spinnerService.hideSpinner();
				reject(err);
			}
		});
	}

	// This method can use other parameters like item and itemKeys, as we are not restricting it with Odata interface.
	async getUsersByAccountIdWithoutSpinner(accountId: string, odata?: any, options?: any): Promise<User[]> {
		return new Promise<User[]>(async (resolve, reject) => {
			let users: User[];
			try {
				const userCollection = await this.getUsersFromAccount(accountId, odata, options);
				if (userCollection) {
					users = Utils.getItemsFromCollection(userCollection);
					const next = Util.getAllNextParamsFromCollection(userCollection);
					console.log('Function: , users: ', users);
					if (next) {
						const moreUsers = await this.getUsersByAccountIdWithoutSpinner(accountId, next, options);
						users = users.concat(moreUsers);
					}
				}
				resolve(users);
			} catch (err) {
				reject(err);
			}
		});
	}

	async getPartner(partnerId: string) {
		return new Promise<Partner>(async (resolve, reject) => {
			try {
				let partner = await this.api.getPartner({partnerId});
				resolve(partner);
			} catch (err) {
				this.errorHandlerSvc.handleError(err);
				reject(err);
			}
		});
	}

	async getUsersFromAccount(accountId: string, odata?: any, options?: any): Promise<UserCollection> {	//odata does not have a searchKeys so 
																								// specified as any instead.		
		return new Promise<UserCollection>(async (resolve, reject) => {
			try {
				let usersCollection = {};
				if (odata) {
				usersCollection = await this.api.getUsersByAccountId({
						accountId, $Skip: odata.$Skip, $Top: odata.$Top, $SkipToken: odata.$SkipToken,
						$Filter: odata.$Filter, $Search: odata.$Search, $SearchKeys: odata.$SearchKeys, 
						items: odata.items, itemKeys: odata.itemKeys, $Expand: odata.$Expand, $Orderby: odata.$Orderby,options
					});
				} else {
					usersCollection = await this.api.getUsersByAccountId({ accountId, options });
				}
				resolve(usersCollection);
			} catch (err) {
				this.errorHandlerSvc.handleError(err);
				reject(err);
			}
		});
	}

	async getSpecificUsersFromAccount(accountId: string, items: string[], itemKeys: string, odata?: any, options?: any): Promise<Array<User>> {
		return new Promise<Array<User>>(async (resolve, reject) => {
			try {
				let users = [];
				let itemList = '';
				for(let i = 0; i< items.length; i++) {
					if ( ((i + 1) === items.length) || (i+1)%50 === 0 ) {
						itemList += items[i];
						const userCollection = await this.getUsersFromAccount(accountId, {...odata, items: itemList, itemKeys: itemKeys}, options);
						if(userCollection && userCollection._embedded && userCollection._embedded.items && userCollection._embedded.items.length > 0) {
							users.push(...userCollection._embedded.items);
						}
						itemList = '';
					} else {
						itemList += items[i] + ',';
					}
				}
				resolve(users);
			} catch (err) {
				reject(err);
			}
		});
	}

	public createUser(body: UserPostRequest): Promise<User> {
		return this.api.createUser({body});
	}

	public updateUserPhoto(userId: string, fileType: string, file: any): Promise<any> {
		const response = this.api.updateUserPhotoWithFileType({ userId: userId, body: file, fileType: fileType});
		return response;
	}

	public getUser(userId: string, expand?: string, options?: any): Promise<User> {
		return new Promise<User>(async (resolve, reject) => {
			try {
				let result = await this.getUserWithoutFrills(userId, expand, options);
				resolve(result);
			} catch (err) {
				this.errorHandlerSvc.handleError(err);
				this.handleUserError(err);
				reject(err);
			}
		});
	}

	public getUserWithoutFrills(userId: string, expand?: string, options?: any): Promise<User> {
		let params = expand ? ( options ? {userId, $Expand: expand, options: options} : {userId, $Expand: expand} ) :
					(options ? {userId, $Expand: expand, options: options} : {userId} );
		return this.api.getUser(params);
	}

	public getSites(accountId: string, odata?: Odata, options?: any): Promise<SiteCollection> {
		if (odata) {
			return this.api.getSites({ accountId, $Top: odata.$Top, $Skip: odata.$Skip,
					 $Expand: odata.$Expand, $SkipToken: odata.$SkipToken, options });
		} else {
				return this.api.getSites({ accountId, options });
		}
	}

	// returns the first siteId for an account
	public getSiteIdForAccount( accountId: string ): Promise<string>
	{
		console.log("Requesting the siteId for account", accountId)
		return this.getSites(accountId).then(siteCollection => {
			if ( siteCollection && siteCollection.count > 0 && siteCollection._embedded && siteCollection._embedded.items ) 
			{
				let sites = siteCollection._embedded.items;
				console.log("All the sites for account", sites)
				if (sites[0]) {
					return sites[0].siteId;
				}
			}
			else
			{
				return undefined;
			}
		});
	}

	public updateUserByAccountId(accountId: string, userId: string, body: UserPutRequest): Promise<User> {
		this.spinnerService.showSpinner();
		this.spinnerService.setSpinnerMessageByKey('spinnerMessages.updatingUser');
		return new Promise<User>(async (resolve, reject) => {
			try {
				const updatedUser = await this.api.updateUserByAccountId({accountId, userId, body});
				this.spinnerService.hideSpinner();
				this.notifier.notify('success', this.translateService.instant('users_edit_page.updateSuccess'));
				resolve(updatedUser);
			} catch (err) {
				this.errorHandlerSvc.handleError(err);
				this.handleUserError(err);
				reject(err);
			}
		});
	}

	public updateUserByAccountIdWithoutSpinnerAndNotify(accountId: string, userId: string, body: UserPutRequest): Promise<User> {
		return new Promise<User>(async (resolve, reject) => {
			try {
				const updatedUser = await this.api.updateUserByAccountId({accountId, userId, body});
				resolve(updatedUser);
			} catch (err) {
				reject(err);
			}
		});
	}


	public createUserForAccount(accountId: string, body: UserPostRequest): Promise<User> {
		this.spinnerService.showSpinner();
		this.spinnerService.setSpinnerMessageByKey('spinnerMessages.creatingUser');
		return new Promise<User>(async (resolve, reject) => {
			try {
				let result = await this.api.createUserByAccountId({accountId, body});
				this.spinnerService.hideSpinner();
				resolve(result);
			} catch (err) {
				this.errorHandlerSvc.handleError(err);
				this.spinnerService.hideSpinner();
				this.handleUserError(err);
				reject(err);
			}
		});
	}

	public createUserForAccountWithoutSpinner(accountId: string, body: UserPostRequest): Promise<User> {
		return new Promise<User>(async (resolve, reject) => {
			try {
				let result = await this.api.createUserByAccountId({accountId, body});
				resolve(result);
			} catch (err) {
				this.errorHandlerSvc.handleError(err);
				this.handleUserError(err);
				reject(err);
			}
		});
	}

	sendWelcomeEmailWithoutNotify(accountId: string, userId: string, body: any): Promise<any> 
	{
		return this.api.sendWelcomeToUserByAccountId({accountId, userId, body});
	}

	// this is only for users that are verified and have been asigned a Miteam Meetings license
	sendMiteamMeetingOnlyWelcomeEmail(user: User): Promise<any>
	{
		let req: any = {
			templateId: "meetingsInfo",
			body: {
				to: [],
				templateProperties: {
					userId: user.uniqueUserId
				}
			}
		};
		return this.api.sendTemplatedEmail(req);
	}

	// this is only for users that are verified and have been asigned a Mitel One license
	sendMitelOneOnlyWelcomeEmail(user: User): Promise<any> {
		let req: any = {
            templateId: "mitelOneInfo",
            body: {
                to: [],
                templateProperties: {
                    userId: user.uniqueUserId
                }
            }
        };
		return this.api.sendTemplatedEmail(req);
	}

	public sendWelcomeEmail(accountId: string, userId: string): Promise<any> {
		let body = {application: "accounts"};
		return new Promise<User>(async (resolve, reject) => {
			try {
				let result = await this.api.sendWelcomeToUserByAccountId({accountId, userId, body});
				this.notifier.notify('success', this.translateService.instant('users_right_panel.welcomeEmailSuccess'));
				resolve(result);
			} catch (err) {
				let message = 'errors.sendUserWelcome.default';
				if (err.body && err.body.name === "EmailNotFound") {
					message = `errors.sendUserWelcome.${err.body.name}`;
				}
				this.notifier.notify('error', this.translateService.instant(message));
				this.errorHandlerSvc.handleError(err);
				reject(err);
			}
		});
	}

	public sendWelcomeEmailWithProvidedBody(accountId: string, userId: string, body: any): Promise<any> {
		return new Promise<User>(async (resolve, reject) => {
			try {
				let result = await this.api.sendWelcomeToUserByAccountId({accountId, userId, body});
				this.notifier.notify('success', this.translateService.instant('users_right_panel.welcomeEmailSuccess'));
				resolve(result);
			} catch (err) {
				let message = 'errors.sendUserWelcome.default';
				if (err.body && err.body.name === "EmailNotFound") {
					message = `errors.sendUserWelcome.${err.body.name}`;
				}
				this.notifier.notify('error', this.translateService.instant(message));
				this.errorHandlerSvc.handleError(err);
				reject(err);
			}
		});
	}

	resetUserPassword(userId: string, body: ResetUserPasswordRequest): Promise<any> {
		return new Promise(async (resolve, reject) => {
			try {
				let result = await this.api.resetUserPassword({userId, body});
				this.notifier.notify('success', this.translateService.instant('users_right_panel.resetPasswordSuccess'));
				resolve(result);
			} catch (err) {
				this.errorHandlerSvc.handleError(err);
				this.handleResetPasswordError(err);
				reject(err);
			}
		});
	}

	resetUserPasswordWithoutNotify(userId: string, body: ResetUserPasswordRequest): Promise<any> {
		return this.api.resetUserPassword({userId, body});
	}

	public getUserByEmailAddress( accountId: string, email: string ):  Promise<User>
	{
		return this.api.getUserByAccountId({accountId: accountId, userId: email});
	}

	public userHasADuplicateEmail( accountId: string, user: User ): Promise<boolean>
	{
		return this.getUserByEmailAddress( accountId, user.email ).then( userWithDupEmail => {
			if ( userWithDupEmail.userId == user.userId )
				return false;
		})
		.catch(error => {
			console.log("Here are the multiple users with email", error);
			// if we get here that means there is more than 1 user with the same email
			if ( error.statusCode == 409 && error.body && error.body.message.indexOf("There are multiple users with ID") != -1 ) 
				return true;
		
			return false;
		});
	}

	public getUserFromAccount(accountId: string, userId: string): Promise<User> {
		this.spinnerService.showSpinner();
		this.spinnerService.setSpinnerMessageByKey('spinnerMessages.fetchingUser');
		return new Promise<User>(async (resolve, reject) => {
			try {
				let result = await this.api.getUserByAccountId({accountId, userId});
				this.spinnerService.hideSpinner();
				resolve(result);
			} catch (err) {
				this.errorHandlerSvc.handleError(err);
				this.handleUserError(err);
				reject(err);
			}
		});
	}

	public getUserForAccountNoFrills(accountId: string, userId: string): Promise<User> {
		return this.api.getUserByAccountId({accountId, userId});
	}

	public getEntity( entityId: string ): Promise<any>
	{
		return this.api.getEntitiesById({entityId: entityId});
	}

	public createAccount(body: AccountPostRequest): Promise<Account> {
		this.spinnerService.showSpinner();
		this.spinnerService.setSpinnerMessageByKey('spinnerMessages.creatingAccount');
		return new Promise<Account>(async (resolve, reject) => {
			try {
				let account = await this.api.createAccount({body});
				resolve(account);
			} catch (err) {
				this.errorHandlerSvc.handleError(err);
				let errorMsg;
				if (typeof err === 'string') {
					errorMsg = err;
				} else if (err instanceof Error) {
					console.error('failed to create account', err.message);
					if (err.message === commonErrorMessages.TYPE_ERROR) {
						errorMsg = accountMessages.ERR_CREATE_ACCOUNT;
					} else {
						console.error('failed to create account', err.message);
						errorMsg = err.message;
					}
				} else {
					console.error('failed to create account', err);
					if (err && err.body && err.body.name) {
						errorMsg = 'errors.createAccount.' + err.body.name;
					} else {
						errorMsg = err.message || err;
					}
				}
				this.spinnerService.hideSpinner();
				console.error('failed createAccount: PARSED , errorMsg: ', errorMsg);
				this.notifier.notify('error', this.translateService.instant(errorMsg));
				reject(err);
			}
		});
	}

	public createContacts(accountId: string, body: ContactPostRequest) {
		return new Promise<ContactCollection>(async (resolve, reject) => {
			try {
				let contacts = await this.api.createAccountContacts({accountId, body});
				this.spinnerService.hideSpinner();
				resolve(contacts);
			} catch (err) {
				this.spinnerService.hideSpinner();
				this.errorHandlerSvc.handleError(err);
				let errorMsg;
				if (typeof err === 'string') {
					errorMsg = err;
				} else if (err instanceof Error) {
					console.error('failed to create contacts', err.message);
					if (err.message === commonErrorMessages.TYPE_ERROR) {
						errorMsg = contactMessages.ERR_CREATE_CONTACT;
					} else {
						console.error('failed to create contacts', err.message);
						errorMsg = err.message;
					}
				} else {
					console.error('failed to create contacts', err);
					if (err && err.name && err.name === contactMessages.INVALID_EMAIL_ADDRESS) {
							errorMsg = 'errors.createAccount.' + err.name;
					} else {
						errorMsg = err.message || err;
					}
				}
				console.error('failed createContact: PARSED , errorMsg: ', errorMsg);
				reject(errorMsg);
			}
		});
	}

	public deleteContact(contactId: string) {
		return new Promise<any>(async (resolve, reject) => {
			try {
				await this.api.deleteContact({contactId});
				resolve(contactId + " Successfully deleted");
			} catch (err) {
				this.errorHandlerSvc.handleError(err);
				let errorMsg;
				if (typeof err === 'string') {
					errorMsg = err;
				} else if (err instanceof Error) {
					console.error('failed to delete contact', err.message);
					if (err.message === commonErrorMessages.TYPE_ERROR) {
						errorMsg = contactMessages.ERR_DELETING_CONTACT;
					} else {
						errorMsg = err.message;
					}
				} else {
					console.error('failed to delete contact', err);
					if (err && err.name && err.name === contactMessages.CONTACT_NOT_FOUND) {
							errorMsg = 'errors.updateAccount.' + err.name;
					} else {
						errorMsg = err.message || err;
					}
				}
				console.error('failed deleteContact: PARSED , errorMsg: ', errorMsg);
				this.notifier.notify('error', this.translateService.instant(errorMsg));
				reject(err);
			}
		});
	}

	// created to be used by the support portal. It is specific to the specified account and not the token
	// Other areas can use it but be aware if you make any changes to make sure you have not broken the support portal!
	public getAllContactsForAccountId(acctId: string, odata?: Odata): Promise<Contact[]> {
		return new Promise<Contact[]>(async (resolve, reject) => {
			let contacts: Contact[] = [];
			try 
			{
				if (!odata) {
					odata = { $Filter: "contactType eq 'ON_SITE' or contactType eq 'MAINTAINER' or contactType eq 'TECHNICAL_SUPPORT'"};
				}
				const contactCollection = await this.getContactsForAccountId(acctId, odata);
				if (contactCollection) {
					contacts = Utils.getItemsFromCollection(contactCollection);
					const next = Utils.getOdataNext(contactCollection);
					console.log('Function: getAllContactsForAccountId, contacts: ', contacts);
					if (next) {
						const moreContacts = await this.getAllContactsForAccountId(acctId, next);
						contacts = contacts.concat(moreContacts);
					}
				}
				resolve(contacts);
			} catch (err) {
				reject(err);
			}
		});

	}

	// created to be used by the support portal. It is specific to the specified account and not the token
	// Other areas can use it but be aware if you make any changes to make sure you have not broken the support portal!
	getContactsForAccountId( acctId: string, odata?: Odata )
	{
		return new Promise<ContactCollection>(async (resolve, reject) => {
			try {
				let result;
				if ( odata ) {
					result = await this.api.getAccountContacts({
						accountId: acctId, 
						$Skip: odata.$Skip, 
						$Top: odata.$Top, 
						$SkipToken: odata.$SkipToken,
						$Filter: odata.$Filter, 
						$Search: odata.$Search});
				} else {
					result = await this.api.getAccountContacts( {accountId: acctId} );
				}
				
				if (result && result.count > 0 && result._embedded && result._embedded.items) {
					resolve(result);
				} else {
					resolve(null);
				}
				
			} catch (err) {
				this.errorHandlerSvc.handleError(err);
				reject(err);
			}
		});
	}

	public getAllContacts(odata?: Odata, options?: any): Promise<Contact[]> {
	// leaving commented out until I know for sure this works...so far so good.
	//	this.spinnerService.showSpinner();
	//	this.spinnerService.setSpinnerMessageByKey('spinnerMessages.loading_wait');
		return new Promise<Contact[]>(async (resolve, reject) => {
			let contacts: Contact[] = [];
			try {
				if (!odata) {
					odata = { $Filter: "contactType eq 'ON_SITE' or contactType eq 'MAINTAINER' or contactType eq 'TECHNICAL_SUPPORT'"};
				}
				const contactCollection = await this.getContacts(odata, options);
				if (contactCollection) {
					contacts = Utils.getItemsFromCollection(contactCollection);
					const next = Utils.getOdataNext(contactCollection);
					console.log('Function: , contacts: ', contacts);
					if (next) {
						const moreContacts = await this.getAllContacts(next, options);
						contacts = contacts.concat(moreContacts);
					}
				}
	//			this.spinnerService.hideSpinner();
				resolve(contacts);
			} catch (err) {
	//			this.spinnerService.hideSpinner();
				reject(err);
			}
			
		});
	}

	public getContacts(odata?: Odata, options?: any) {
		return new Promise<ContactCollection>(async (resolve, reject) => {
			try {
				let result;
				if (odata) {
					result = await this.api.getContacts({$Skip: odata.$Skip, $Top: odata.$Top, $SkipToken: odata.$SkipToken,
					$Filter: odata.$Filter, $Search: odata.$Search, options});
				} else {
					result = await this.api.getContacts({ options});
				}
				
				if (result && result.count > 0 && result._embedded && result._embedded.items) {
					resolve(result);
				} else {
					resolve(null);
				}
				
			} catch (err) {
				this.errorHandlerSvc.handleError(err);
				reject(err);
			}
		});
	}


	public getAccount(accountId: string): Promise<Account> {
		console.log('Function: getAccount, GET ACCOUNT:');
		this.spinnerService.showSpinner();
		this.spinnerService.setSpinnerMessageByKey('spinnerMessages.fetchingAccount');
		return new Promise<Account>(async (resolve, reject) => {
			try {
				let account = await this.api.getAccount({accountId});
				this.spinnerService.hideSpinner();
				resolve(account);
			} catch (err) {
				this.errorHandlerSvc.handleError(err);
				reject(err);
			}
		});
	}

	public getAccountWithoutFrills(accountId: string, expand?: string, options?: any): Promise<Account> {
		let params = expand ? ( options ? {accountId, $Expand: expand, options: options} : {accountId, $Expand: expand} ) :
					(options ? {accountId, options: options} : {accountId} );
		return this.api.getAccount(params);
	}

	public getAllAccounts(odata?: Odata): Promise<Account[]> {
		this.spinnerService.showSpinner();
		this.spinnerService.setSpinnerMessageByKey('spinnerMessages.fetchingAccounts');
		return new Promise<Account[]>(async (resolve, reject) => {
			let accounts: Account[];
			try {
				console.log('Function: , odata: ', odata);
				const accountCollection = await this.getAccounts(odata);
				if (accountCollection && accountCollection.count > 0 && accountCollection._embedded && accountCollection._embedded.items) {
					accounts = accountCollection._embedded.items;
					const next = Utils.getOdataNext(accountCollection);
					if (next && next.$SkipToken) {
						const moreAccounts = await this.getAllAccounts(next);
						if (moreAccounts) {
							accounts = accounts.concat(moreAccounts);
						}
					}
				}
				this.spinnerService.hideSpinner();
				resolve(accounts);
			} catch (err) {
				this.errorHandlerSvc.handleError(err);
				reject(err);
			}
		});
	}

	public getAllAccountsWithoutFrills(odata?: Odata): Promise<Account[]> {
		return new Promise<Account[]>(async (resolve, reject) => {
			let accounts: Account[];
			try {
				const accountCollection = await this.getAccounts(odata);
				if (accountCollection && accountCollection.count > 0 && accountCollection._embedded && accountCollection._embedded.items) {
					accounts = accountCollection._embedded.items;
					const next = Utils.getOdataNext(accountCollection);
					if (next && next.$SkipToken) {
						const moreAccounts = await this.getAllAccountsWithoutFrills(next);
						if (moreAccounts) {
							accounts = accounts.concat(moreAccounts);
						}
					}
				}
				resolve(accounts);
			} catch (err) {
				reject(err);
			}
		});
	}

	public getAllAccountsWithTags(odata?: Odata): Promise<Account[]> {
		this.spinnerService.showSpinner();
		this.spinnerService.setSpinnerMessageByKey('spinnerMessages.fetchingAccounts');
		return new Promise<Account[]>(async (resolve, reject) => {
			let accounts: Account[];
			try {
				console.log('Function: , odata: ', odata);
				const accountCollection = await this.getAccountsWithTags(odata);
				if (accountCollection && accountCollection.count > 0 && accountCollection._embedded && accountCollection._embedded.items) {
					accounts = accountCollection._embedded.items;
					const next = Utils.getOdataNext(accountCollection);
					if (next) {
						const moreAccounts = await this.getAllAccountsWithTags(next);
						if (moreAccounts) {
							accounts = accounts.concat(moreAccounts);
						}
					}
				}
				this.spinnerService.hideSpinner();
				resolve(accounts);
			} catch (err) {
				this.spinnerService.hideSpinner();
				this.errorHandlerSvc.handleError(err);
				reject(err);
			}
		});
	}

	public getAccounts(odata?: Odata, options?: any): Promise<AccountCollection> {
		if (odata) {
			return this.api.getAccounts({
				$Expand: odata.$Expand, $Skip: odata.$Skip,
				$Top: odata.$Top, $SkipToken: odata.$SkipToken, options
			});
		} else {
			return this.api.getAccounts({ options });
		}
	}

	public getAccountsWithTags(odata?: Odata, options?: any): Promise<AccountCollection> {
		if (odata) {
			return this.api.getAccountsWithTags({
				$Skip: odata.$Skip,
				$Top: odata.$Top, $SkipToken: odata.$SkipToken, options
			});
		} else {
			return this.api.getAccountsWithTags({ options });
		}
	}

	public async tryUpdateAccountTags(accountId: string, tags: any,  body: any): Promise<any> {
		const existingTags = tags || {};
		const updatedTags = {};
		let newTags;
		for (const updatedTagId of Object.keys(body)) {
			if (existingTags[updatedTagId] !== undefined) {
				if (!_.isEqual(existingTags[updatedTagId], body[updatedTagId])) {
					const tagValue = typeof (body[updatedTagId]) === 'string' ? JSON.stringify(body[updatedTagId]) : body[updatedTagId];
					const response = await this.updateAccountTag(accountId, updatedTagId, tagValue);
					delete response._links; // removes the _links property
					updatedTags[updatedTagId] = response;
				}
			} else {
				if (!newTags) {
					newTags = {};
				}
				newTags[updatedTagId] = body[updatedTagId];
			}
		}
		if (newTags) {
			return await this.createAccountTag(accountId, newTags);
		}
		return { ...existingTags, ...updatedTags };
	}

	public updateAccountTag(accountId: string, tagId: string, body: any): Promise<Tag> {
		this.accountTagsCache.delete(accountId);
		return this.api.updateAccountTag({ accountId, tagId, body });
	}


	public getAccountTags(accountId: string): Promise<any> {
		return this.getAccountTagsNoFrills(accountId).then( tags => {
			return tags;
		})
		.catch (err => {
			this.errorHandlerSvc.handleError(err);
			throw(err);
		});
	}

	/**
	 * No Frills, means no error handling
	 * @param accountId 
	 * @param forceRefresh ignores cache and forces a lookup
	 * @returns tags or undefined
	 */
	public getAccountTagsNoFrills(accountId: string, forceRefresh: boolean = false): Promise<any> {
		if (forceRefresh || !this.accountTagsCache.has(accountId)) {
			const promise = this.api.getAccountTags({ accountId })
				.then(
					result => result,
					error => {
						if (error.body?.name === 'NoAccountTags') {  // 404
							console.info(`${logContext}.getAccountTagsNoFrills  NoAccountTags`, error);
							return {}; // CLSD-3702
						}
						this.accountTagsCache.delete(accountId);  // remove rejected Promise from cache
						throw error;
					}
				);
			this.accountTagsCache.set(accountId, promise);
		}
		return this.accountTagsCache.get(accountId);
	}

	public createAccountTagNoFrills(accountId: string, body: TagPostRequest): Promise<any> {
		this.accountTagsCache.delete(accountId);
		return this.api.createAccountTag({accountId, body});
	}

	public createAccountTag(accountId: string, body: TagPostRequest): Promise<any> {
		this.spinnerService.showSpinner();
		this.spinnerService.setSpinnerMessageByKey('spinnerMessages.working');
		return new Promise<any>(async (resolve, reject) => {
			try {
				this.accountTagsCache.delete(accountId);
				const tags = await this.api.createAccountTag({accountId, body});
				this.spinnerService.hideSpinner();
				resolve(tags);
			} catch (err) {
				this.errorHandlerSvc.handleError(err);
				let errorMsg;
				if (err instanceof Error) {
					if (err.message === commonErrorMessages.TYPE_ERROR) {
						errorMsg = commonMessages.ERR_PROGRESS;
					} else {
						errorMsg = err.message;
					}
				} else {
					if (err && err.body) {
						errorMsg = err.body.name ? ('errors.createAccountTag.' + err.body.name) : commonMessages.ERR_PROGRESS;
					}
				}
				this.spinnerService.hideSpinner();
				console.error('failed to set account tag: PARSED , errorMsg: ', errorMsg);
				this.notifier.notify('error', this.translateService.instant(errorMsg));
				reject(err);
			}
		});
	}

	async createAppGroup(number: string, name?: string) {
		const body: GroupPostRequest = {
      		name: name || number,
     		dialableNumber: number,
      		type: "APP",
      		visibility: name ? "PUBLIC" : "PRIVATE",
    		};

    	const g = await this.api.createGroup({ body: body });
	}

	async deleteAppGroup(accountId: string, groupId: string) {
		return new Promise<any>(async (resolve, reject) => {
			try {
				await this.api.deleteGroupByAccountId({ accountId: accountId, groupId: groupId });
				resolve(groupId + " Successfully deleted");
			} catch (err) {
				this.errorHandlerSvc.handleError(err);
				let errorMsg;
				if (typeof err === 'string') {
					errorMsg = err;
				} else if (err instanceof Error) {
					console.error('failed to delete app group', err.message);
					errorMsg = err.message;
				} else {
					console.error('failed to delete app group', err);
					errorMsg = err.message || err;
				}
				console.error('failed deleteAppGroup: PARSED , errorMsg: ', errorMsg);
				this.notifier.notify('error', this.translateService.instant(errorMsg));
				reject(err);
			}
		});
	}

	async getAppGroups(accountId: string, filter?: string): Promise<GroupCollection> {
		return this.api.getGroupsByAccountId({ accountId: accountId, $Filter: filter })
	}

	public updateAccountWithoutSpinner(accountId: string, body: any): Promise<Account>
	{
		this.accountTagsCache.delete(accountId);
		return this.api.updateAccount({accountId, body});
	}

	public updateAccount(accountId: string, body: any): Promise<Account> { // Temporarily changing body type as policyId is not currently supported
		this.spinnerService.showSpinner();
		this.spinnerService.setSpinnerMessageByKey('spinnerMessages.updatingAccount');
		return new Promise<any>( async (resolve, reject) => {
			try {
				this.accountTagsCache.delete(accountId);
				const updatedAccount = await this.api.updateAccount({accountId, body});
				this.spinnerService.hideSpinner();
				resolve(updatedAccount);
			} catch (err) {
				this.errorHandlerSvc.handleError(err);
				let errorMsg = ': ';
				let translateErrMsg = accountMessages.ERR_UPDATE_ACCOUNT;
				if (typeof err === 'string') {
					errorMsg +=  err;
				} else if (err instanceof Error) {
					console.error('failed to update account', err.message);
					if (err.message === commonErrorMessages.TYPE_ERROR) {
						translateErrMsg = accountMessages.ERR_UPDATE_ACCOUNT;
						errorMsg = '';
					} else {
						console.error('failed to update account', err.message);
						errorMsg = err.message;
					}
				} else {
					console.error('failed to update account', err);
					if (err && err.body && err.body.name) {
							errorMsg = err.body.name;
					} else if (err && err.body && err.body.message) {
						errorMsg = err.body.message;
					} else {
						errorMsg = err.message || err;
					}
				}
				console.error('failed updateAccount: PARSED , errorMsg: ', errorMsg);
				this.spinnerService.hideSpinner();
				this.notifier.notify('error', this.translateService.instant(translateErrMsg) + errorMsg);
				reject(err);
			}
		});
	}

	public  deleteAccount(accountId: string, hard?: string, options?: any): Promise<any> {
		this.spinnerService.showSpinner();
		this.spinnerService.setSpinnerMessageByKey(hard ? 'spinnerMessages.deletingAccount' : 'spinnerMessages.deactivatingAccount');
		return new Promise<any>(async (resolve, reject) => {
			try {
				await this.api.deleteAccount({accountId, hard, options});
				this.spinnerService.hideSpinner();
				this.notifier.notify('success', this.translateService.instant(
					hard ? 'account.deleteAccountMessage' : 'account.disableAccountMessage'
				));
				resolve(accountId);
			} catch (err) {
				this.errorHandlerSvc.handleError(err);
				this.spinnerService.hideSpinner();
				this.notifier.notify('error', this.translateService.instant(hard ? 'errors.deleteAccount' : 'errors.disableAccount'));
				reject(err);
			}
		});
	}

	setSsoPolicyForAccount( account: Account, disableNativeUserLogin: boolean ): Promise<Account>
	{
		// we will only set the value if it has changed
		// using any casting because the property is not available in the sdk yet
		let tempAccount: any = account;
		if ( !tempAccount.policy || 
			!tempAccount.policy.disableNativeUserLogin || 
			(tempAccount.policy.disableNativeUserLogin != disableNativeUserLogin) )
		{
			let inputParams: any = {
				accountId: account.accountId, 
				body: { 
					policy:{
						disableNativeUserLogin: disableNativeUserLogin
					}
				} 
			}
			return this.api.updateAccount(inputParams);
		}
		else
		{
			return Promise.resolve(account); // nothing is changing
		}
	}
	
	public checkIfUserIsEligibleForDelete( user: User ) 
	{
		let sourceState = user.source === 'APP' ||
						  user.source === 'PBX' ||
						  user.source === 'SCIM' || 
						  user.source === 'IOT_DEVICE';
		let roleState = user.role !== 'PARTNER_ADMIN'; // no one knows why the account admn was included row.role !== 'ACCOUNT_ADMIN' && row.role !== 'PARTNER_ADMIN';
		return sourceState && roleState;
	};

	public deleteUserByAccountIdWithoutSpinnerNotify(accountId: string, user: User): Promise<any> 
	{
		return this.api.deleteUserByAccountId( {accountId, userId: user.userId} ).catch((err) => {
			if (err.statusCode == 404) {
				// User not found - not necessarily a problem - treat as success
				console.warn("Could not delete user: user not found. User may already be deleted.", err);
				return;
			} else {
				throw(err);
			}
		});
	}

	public deleteUserByAccountId(accountId: string, user: UserData): Promise<any> {
		this.spinnerService.showSpinner();
		this.spinnerService.setSpinnerMessageByKey('spinnerMessages.deletingUser');
		return new Promise<any>(async (resolve, reject) => {
			try {
				let result = await this.api.deleteUserByAccountId({accountId, userId: user.id});
				console.log('deleteUserByAccountId result:', result);
				let displayMessage = user.displayName ? `${user.displayName} - ${this.translateService.instant('users_list.deleteUserMessage')}` :  `${this.translateService.instant('users_list.deleteUserMessage')}`;   
				this.notifier.notify('success', displayMessage);
				this.spinnerService.hideSpinner();
				resolve(user.id);
			} catch (err) {
				if (err.statusCode == 404) {
					// User not found - not necessarily a problem - treat as success
					console.warn("Could not delete user: user not found. User may already be deleted.", err);
					let displayMessage = user.displayName ? `${user.displayName} - ${this.translateService.instant('users_list.deleteUserMessage')}` :  `${this.translateService.instant('users_list.deleteUserMessage')}`;   
					this.notifier.notify('success', displayMessage);
					resolve(user.id);
				} else {
					this.errorHandlerSvc.handleError(err);
					this.notifier.notify('error', this.translateService.instant('errors.deleteUser'));
					reject(err);
				}
				this.spinnerService.hideSpinner();
			}
		});
	}

	private handleUserError(err: any) {
		// if (err.statusCode !== 409) {
			let errorMsg = accountMessages.ERR_CREATE_ADMIN;
			if (err.body && err.body.name) {
				errorMsg = 'errors.createOrUpdateNewUser.' + err.body.name;
			}
			this.notifier.notify('error', this.translateService.instant(errorMsg));
			console.error('Function handleUserError: PARSED , errorMsg: ', errorMsg);
		// }
	}

	private handleResetPasswordError(err: any) {
		let errorMsg = 'errors.resetPassword.default';
		if (err.body && err.body.name) {
			errorMsg = 'errors.resetPassword.' + err.body.name;
		}
		this.notifier.notify('error', this.translateService.instant(errorMsg));
	}

	getADSyncKeys( accountId: string, type: string ): Promise<any>
	{
		let params: any = {
			accountId: accountId,
			body: {
				type: type
			}
		};
		return this.api.createScimKey(params);
	}

	
	doesScimKeyExist( accountId: string ): Promise<boolean>
	{
		let params: any = {
			accountId: accountId,
		};
		return this.api.scimKeyExists(params).then(result => {
			console.log("The key exists", result);
			return true;
		}, error => {
			console.log("This is the error", error);
			if ( error.statusCode === 404 ) // stated false
			{
				return false;
			}
			else if ( error.statusCode === 204 ) // stated true
				return true;
			else
				return false; // all else gets a false
		});
	}

	clearADSyncKeys( accountId: string, type: string ): Promise<any>
	{
		return this.getADSyncKeys( accountId, type );
    }
    
    async getPolicy(accountId: string, policyId: string): Promise<AccessPolicy> {
        const params = {
            accountId,
            policyId
        };
        
        return this.api.getAccountAccessPolicy(params);
    }
	
	removeStatementFromPolicy( accountId: string, policyId: string, statementId: string): Promise<any>
	{
		const params = {
			accountId,
			policyId,
			statementId
		}

		return this.api.deleteAccountAccessPolicyStatement(params);
	}

  async updateUserPolicyStatement(accountId: string, policyId: string, statement: Statement): Promise<Statement> {
    const params = {
      accountId,
      policyId,
      body: statement
    };
    return this.api.updateAccountAccessPolicyStatement(params);
  }
    
    async createUserPolicy(policy: PolicyPostRequest, accountId: string): Promise<AccessPolicy> {
        const params = {
            accountId: accountId,
            body: policy
        };
        return this.api.createAccountAccessPolicy(params);
	}

	async updatePolicy(accountId: string, policyId: string, name?: string, statements?: Array<Statement>) {
		const params = {
			accountId,
			policyId,
			body: {} as PolicyPutRequest
		};

		if (name !== undefined && name !== null) {
			params.body.name = name;
		}
		if (statements) {
			params.body.statements = statements
		}

		return this.api.updateAccountAccessPolicy(params);
	}

	removeSpecifiedStatementFromAccountPolicy( account: Account, statement: string ): Promise<any>
	{
		if ( account['policyId'] != undefined )
		{
			return this.getPolicy(account.accountId, account['policyId']).then( policy => {
				console.log("Before statement removal: Here are the account policy", policy);
				// now find the statement and remove it
				const foundPolicy = policy.statements.find(stmt => {
					return stmt.statementId === statement;
				});
				if ( foundPolicy )
				{
					//remove the statement
					return this.removeStatementFromPolicy( account.accountId, policy.policyId, 
						foundPolicy.statementId).then(data => {
						console.log("After removal: this is the returned data", data)
						return data;
					});
				}
				else
 					return policy;
			})
		}
		else
		{
			return Promise.resolve(null); // no policy so nothing to do here
		}
	}

	addSpecifiedStatementToAccountPolicy( account: Account, statement: Statement ): Promise<AccessPolicy>
	{
		
		if ( account['policyId'] != undefined ) 
		{
			return this.getPolicy(account.accountId, account['policyId']).then( policy => {
				console.log("Before adding statement: Here are the account policy", policy);
				const foundPolicy = policy.statements.find(stmt => {
					return stmt.statementId === statement.statementId;
				});
				if (foundPolicy) {
					console.log("We found the policy statement already exists")
					// nothing to add, statement already exists
					return policy;
				}
				else
				{
					return this.updatePolicy( account.accountId, policy.policyId, null, [statement] );
				}
			}) 
			.catch(err => {
				console.log("Here is the error from getting policies", err)
				return null;
			});
		}
		else
		{
			// we will create a new policy from scratch
			const policy: PolicyPostRequest = {
				name: account.accountId + '-policy',
				statements: [
					statement
				]
			};
			return this.createUserPolicy(policy, account.accountId);
		}
	}
	
	deleteAccountTag( accountId: string, tagKey: string ): Promise<any>
	{
		let params = {
			accountId: accountId,
			tagId: tagKey
		}
		this.accountTagsCache.delete(accountId);
		return this.api.deleteAccountTag(params);
	}
	
	sendEmailByTemplateId(templateId: String, user: User, body?: any): Promise<any>
	{
		let req: any;
		if(body){
			req = {
				templateId: templateId,
				body: body
			};
		}
		else{
			req = {
				templateId: templateId,
				body: {
					to: [],
					templateProperties: {
						userId: user.uniqueUserId
					}
				}
			};
		}
		return this.api.sendTemplatedEmail(req);
	}

    getAllAccountPolicies(accountId: string, odata?: Odata): Promise<AccessPolicy[]> {
        return new Promise<AccessPolicy[]>(async (resolve, reject) => {
            let policies: AccessPolicy[] = [];
            try {
                console.log(`Function: getAllAccountPolicies, odata: ${odata}`);
                const policyCollection = await this.getAccountPolicies(accountId, odata);
                if (policyCollection && policyCollection.count > 0 && policyCollection._embedded && policyCollection._embedded.items) {
                    policies = policyCollection._embedded.items;
                    const next = Utils.getOdataNext(policyCollection);
                    if (next){
                        const morePolicies = await this.getAllAccountPolicies(accountId, next);
                        if (morePolicies) {
                            policies = policies.concat(morePolicies);
                        }
                    }
                }
                resolve(policies);
            } catch (err) {
                this.errorHandlerSvc.handleError(err);
                reject(err);
            }
        });
    }

    getAccountPoliciesCollection(accountId: string, odata?: Odata): Promise<PolicyCollection> {
        return new Promise<PolicyCollection>(async (resolve, reject) => {
            try {
                console.log(`Function: getAllAccountPolicies, odata: ${odata}`);
                const policyCollection = await this.getAccountPolicies(accountId, odata);
                resolve(policyCollection);
            } catch (err) {
                this.errorHandlerSvc.handleError(err);
                reject(err);
            }
        });
    }

    getAccountPolicies(accountId: string, odata?: Odata, options?: any): Promise<PolicyCollection> {
        if (odata) {
            return this.api.getAccountAccessPolicies({
                accountId,
                $Top: odata.$Top,
                $SkipToken: odata.$SkipToken,
                options
            });
        } else {
            return this.api.getAccountAccessPolicies({ accountId, options });
        }
  }

  public async getPoliciesByType(type: string, skipToken?: string): Promise<AccessPolicy[]> {
    return new Promise<any[]>(async (resolve, reject) => {
      let typePolicies: any[] = [];
      try {
        if (type) {
          let queryParams = { $top: '50',  ...(skipToken) && { $skipToken: skipToken }};
          const policiesCollection = await this.adminServiceURL.getPoliciesByType(type, queryParams);
          if (policiesCollection && policiesCollection.count > 0 && policiesCollection._embedded && policiesCollection._embedded.items) {
            typePolicies = policiesCollection._embedded.items;
            if (policiesCollection._links && policiesCollection._links.next) {
              const morePolicies = await this.getPoliciesByType(type, policiesCollection._links.next);
              if (morePolicies) {
                typePolicies = typePolicies.concat(morePolicies);
              }
            }
          }
        }
        resolve(typePolicies);
      } catch (error) {
        reject(error);
      }
    });
  }

	getUserTagsByAccountId(accountId: string, userId: string): Promise<any> {
		this.spinnerService.showSpinner();
		this.spinnerService.setSpinnerMessageByKey('spinnerMessages.working');
		return new Promise<any>(async (resolve, reject) => {
			try {
				const tags = await this.api.getUserTagsByAccountId({accountId, userId});
				this.spinnerService.hideSpinner();
				resolve(tags);
			} catch (err) {
				this.errorHandlerSvc.handleError(err);
				let errorMsg;
				if (err instanceof Error) {
					if (err.message === commonErrorMessages.TYPE_ERROR) {
						errorMsg = commonMessages.ERR_PROGRESS;
					} else {
						errorMsg = err.message;
					}
				} else {
					if (err && err.body) {
						errorMsg = err.body.name ? ('errors.getUserTagsByAccountId.' + err.body.name) : commonMessages.ERR_PROGRESS;
					}
				}
				console.error(`failed to get user tags by account: ${accountId}, user: ${userId}, errorMsg: ${errorMsg}`);
				this.notifier.notify('error', this.translateService.instant(errorMsg));
				reject(err);
			}
		});
	}

	getUserTagsByAccountIdWithoutSpinner(accountId, userId) {
		return new Promise<any>(async (resolve, reject) => {
			try {
				const  result = await this.api.getUserTagsByAccountId({accountId, userId});
				delete result._links;
				console.log('user tags are: ', result);
				resolve(result);
			} catch (err) {
				console.error("Error occured while fetching user tags", err);
				resolve({});
			}
		});
	}

	createUserTagByAccountId(accountId: string, userId: string, body: TagPostRequest): Promise<any> {
		this.spinnerService.showSpinner();
		this.spinnerService.setSpinnerMessageByKey('spinnerMessages.working');
		return new Promise<any>(async (resolve, reject) => {
			try {
				const tag = await this.api.createUserTagByAccountId({accountId, userId, body});
				this.spinnerService.hideSpinner();
				resolve(tag);
			} catch (err) {
				this.errorHandlerSvc.handleError(err);
				let errorMsg;
				if (err instanceof Error) {
					if (err.message === commonErrorMessages.TYPE_ERROR) {
						errorMsg = commonMessages.ERR_PROGRESS;
					} else {
						errorMsg = err.message;
					}
				} else {
					if (err && err.body) {
						errorMsg = err.body.name ? ('errors.createUserTagByAccountId.' + err.body.name) : commonMessages.ERR_PROGRESS;
					}
				}
				console.error(`failed to create user tag by account: ${accountId}, user: ${userId}, errorMsg: ${errorMsg}`);
				this.notifier.notify('error', this.translateService.instant(errorMsg));
				reject(err);
			}
		});
	}

	updateUserTagByAccountId(accountId: string, userId: string, tagId: string, body: TagPostRequest): Promise<any> {
		this.spinnerService.showSpinner();
		this.spinnerService.setSpinnerMessageByKey('spinnerMessages.working');
		return new Promise<any>(async (resolve, reject) => {
			try {
				const tag = await this.api.updateUserTagByAccountId({accountId, userId, tagId, body});
				this.spinnerService.hideSpinner();
				resolve(tag);
			} catch (err) {
				this.errorHandlerSvc.handleError(err);
				let errorMsg;
				if (err instanceof Error) {
					if (err.message === commonErrorMessages.TYPE_ERROR) {
						errorMsg = commonMessages.ERR_PROGRESS;
					} else {
						errorMsg = err.message;
					}
				} else {
					if (err && err.body) {
						errorMsg = err.body.name ? ('errors.updateUserTagByAccountId.' + err.body.name) : commonMessages.ERR_PROGRESS;
					}
				}
				console.error(`failed to update user tag by account: ${accountId}, user: ${userId}, tag: ${tagId}, errorMsg: ${errorMsg}`);
				this.notifier.notify('error', this.translateService.instant(errorMsg));
				reject(err);
			}
		});
	}

	deleteUserTagByAccountId(accountId: string, userId: string, tagId: string): Promise<any> {
		this.spinnerService.showSpinner();
		this.spinnerService.setSpinnerMessageByKey('spinnerMessages.working');
		return new Promise<any>(async (resolve, reject) => {
			try {
				const tag = await this.api.deleteUserTagByAccountId({accountId, userId, tagId});
				this.spinnerService.hideSpinner();
				resolve(tag);
			} catch (err) {
				this.errorHandlerSvc.handleError(err);
				let errorMsg;
				if (err instanceof Error) {
					if (err.message === commonErrorMessages.TYPE_ERROR) {
						errorMsg = commonMessages.ERR_PROGRESS;
					} else {
						errorMsg = err.message;
					}
				} else {
					if (err && err.body) {
						errorMsg = err.body.name ? ('errors.deleteUserTagByAccountId.' + err.body.name) : commonMessages.ERR_PROGRESS;
					}
				}
				console.error(`failed to delete user tag by account: ${accountId}, user: ${userId}, tag: ${tagId}, errorMsg: ${errorMsg}`);
				this.notifier.notify('error', this.translateService.instant(errorMsg));
				reject(err);
			}
		});
	}

	updateUserTagByAccountIdWithoutSpinner(accountId, userId, tagId, body) {
		return this.api.updateUserTagByAccountId({accountId, userId, tagId, body});
	}

	async tryUpdateTagsOfUserByAccountId(accountId, userId, body, existingTags) {
		console.log(body, existingTags)
		const updatedTags = {};
        let newTags;
        for (const updatedTagId of Object.keys(body)) {
          if (existingTags[updatedTagId] !== undefined) {
            if (!_.isEqual(existingTags[updatedTagId], body[updatedTagId])) {
			  const tagValue = typeof(body[updatedTagId]) === 'string' ? JSON.stringify(body[updatedTagId]) : body[updatedTagId];
			  let result = body[updatedTagId];
			  await this.updateUserTagByAccountIdWithoutSpinner(accountId, userId, updatedTagId, tagValue)
			  							.catch( err => {
											console.log("Failed to update tag:" + updatedTagId + " for user: ", userId);
											result = existingTags[updatedTagId];
										  });
              updatedTags[updatedTagId] = result;
            }
          } else {
              if (!newTags) {
                newTags = {};
              }
              newTags[updatedTagId] = body[updatedTagId];
            }
        }
        if (newTags) {
		  return await this.createUserTagByAccountIdWithoutSpinner(accountId, userId, newTags)
						.catch( err => {
							console.log("Failed to create new tags for user with userId", userId);
							return {...existingTags, ...updatedTags};
						});
        }
        return {...existingTags, ...updatedTags};
	}
	
	/**
	* No Frills, means no error handling
	* Same as tryUpdateTagsOfUserByAccountId but without error handling
	*/
	async tryUpdateTagsOfUserByAccountIdNoFrills(accountId, userId, body, existingTags) {
		console.log(body, existingTags)
		const updatedTags = {};
		let newTags;
		for (const updatedTagId of Object.keys(body)) {
			if (existingTags[updatedTagId] !== undefined) {
				if (!_.isEqual(existingTags[updatedTagId], body[updatedTagId])) {
					const tagValue = typeof (body[updatedTagId]) === 'string' ? JSON.stringify(body[updatedTagId]) : body[updatedTagId];
					let result = body[updatedTagId];
					await this.updateUserTagByAccountIdWithoutSpinner(accountId, userId, updatedTagId, tagValue);
					updatedTags[updatedTagId] = result;
				}
			} else {
				if (!newTags) {
					newTags = {};
				}
				newTags[updatedTagId] = body[updatedTagId];
			}
		}
		if (newTags) {
			return await this.createUserTagByAccountIdWithoutSpinner(accountId, userId, newTags);
		}
		return { ...existingTags, ...updatedTags };
	}

	createUserTagByAccountIdWithoutSpinner(accountId, userId, body) {
		return new Promise<any>(async (resolve, reject) => {
			try {
				const  result = await this.api.createUserTagByAccountId({accountId, userId, body});
				delete result._links;
				console.log('user tags are: ', result);
				resolve(result);
			} catch (err) {
				reject(err);
			}
		});
	}

	deleteUserTagByAccountIdWithoutSpinner(accountId: string, userId: string, tagId: string) {
		return this.api.deleteUserTagByAccountId({accountId, userId, tagId});
	}

	createAccountAdminClientWithPolicyId( policyId: string ): Promise<Client>
	{
		let params: ClientPostRequest = {
			name: "Workflow Client for MS Teams Presence",
			role: "ACCOUNT_ADMIN",
			policyId: policyId
		}

		return this.api.createClient( {body: params}).then( client => {
			return client;
		});
	}

	deleteClientById( id: string ): Promise<any>
	{
		return this.api.deleteClient( {clientId: id}).then( result => {
			return result;
		});
	}

	deletePolicyById( accountId: string, policyId: string ): Promise<any>
	{
		return this.api.deleteAccountAccessPolicy({accountId: accountId, policyId:policyId} );
	}

	createClient(accountId: string, name: string, role: ClientPostRequest.RoleEnum) {
		return this.api.createClientByAccountId({
			accountId,
			body: {
				name,
				role
			}
		})
	}

	async createAccountSmsIntegrationTag(accountId: string, clientId: string, outboundFlowId: string, inboundFlowId: string, userAdminFlowId: string, accountAdminFlowId: string, administrators: string[]) {
		this.accountTagsCache.delete(accountId);
		return this.api.createAccountTag({
			accountId,
			body: {
				smsIntegration: {
					smsProviderClientId: clientId,
					smsOutboundFlowId: outboundFlowId,
					smsInboundFlowId: inboundFlowId,
					smsUserAdminFlowId: userAdminFlowId,
					smsAccountAdminFlowId: accountAdminFlowId,
					smsAdministrators: administrators,
				}
			}
		});
	}

	getPolicyControls(options?: any) {
		if (options) {
			return this.api.getPolicyControls({ options: options });
		}
		return this.api.getPolicyControls({});
	}

	getPolicyControl(policyControlId: string) {
		return this.api.getPolicyControl({ policyControlId });
	}

	createPolicyControl(body: PolicyControlPostRequest) {
		return this.api.createPolicyControl({ body });
	}

	deletePolicyControl(policyControlId: string) {
		return this.api.deletePolicyControl({ policyControlId });
	}

	flushAccountTagsCache() {
		this.accountTagsCache.clear();
	}
}
